import React from "react";
import { Helmet } from "react-helmet";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";

import Footer from "./Footer";
import Header from "./Header";
import { blogPosts, getBlogPostBySlug } from "../data/blogPosts";

export default function BlogPostPage() {
  const { slug } = useParams();
  const post = getBlogPostBySlug(slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const canonicalUrl = `https://fold-web-agency.fr/blog/${post.slug}`;
  const relatedPosts = blogPosts.filter((entry) => entry.slug !== post.slug);
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDescription,
    datePublished: post.publishedAt,
    dateModified: post.publishedAt,
    author: {
      "@type": "Organization",
      name: "Fold Web Agency",
      url: "https://fold-web-agency.fr/",
    },
    publisher: {
      "@type": "Organization",
      name: "Fold Web Agency",
      url: "https://fold-web-agency.fr/",
    },
    mainEntityOfPage: canonicalUrl,
  };
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: post.faq.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: item.answer,
      },
    })),
  };
  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: "Accueil",
        item: "https://fold-web-agency.fr/",
      },
      {
        "@type": "ListItem",
        position: 2,
        name: "Blog",
        item: "https://fold-web-agency.fr/blog",
      },
      {
        "@type": "ListItem",
        position: 3,
        name: post.title,
        item: canonicalUrl,
      },
    ],
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }}>
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">
          {JSON.stringify(articleStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbStructuredData)}
        </script>
      </Helmet>

      <Header />

      <main className="blog-post-page">
        <article className="blog-post">
          <div className="container blog-post-container">
            <Link to="/blog" className="portfolio-back-link">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>
            <p className="hero-announcement">{post.category}</p>
            <h1 className="heading-hero">{post.title}</h1>
            <p className="body-large blog-post-intro">{post.intro}</p>
            <p className="caption">{post.readingTime} de lecture</p>

            <div className="blog-post-content">
              {post.sections.map((section) => (
                <section key={section.title}>
                  <h2 className="heading-2">{section.title}</h2>
                  <p className="body-medium">{section.body}</p>
                </section>
              ))}
            </div>
          </div>
        </article>

        <section className="section alt">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">Questions fréquentes</h2>
            </div>
            <div className="seo-faq-grid">
              {post.faq.map((item) => (
                <article className="seo-faq-item" key={item.question}>
                  <h3 className="heading-3">{item.question}</h3>
                  <p className="body-medium">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="section">
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1">À lire aussi</h2>
            </div>
            <div className="blog-grid">
              {relatedPosts.map((relatedPost) => (
                <article className="blog-card" key={relatedPost.slug}>
                  <div className="blog-card-meta">
                    <span>{relatedPost.category}</span>
                    <span>{relatedPost.readingTime}</span>
                  </div>
                  <h3 className="heading-3">
                    <Link to={`/blog/${relatedPost.slug}`}>{relatedPost.title}</Link>
                  </h3>
                  <p className="body-medium">{relatedPost.excerpt}</p>
                  <Link to={`/blog/${relatedPost.slug}`} className="service-learn-more">
                    Lire l'article
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </article>
              ))}
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
